(() => {
  const ROOT_SELECTOR = '[data-mf-product-details]';
  const MEASURE_PATTERN = '\\d{1,4}(?:[.,]\\d{1,2})?\\s*(?:mm|cm|m)';
  const PACKAGING_HEADER_PATTERN = /^(?:dimens[oõ]es|medidas|tamanho)\s+(?:da|do|de)\s+(?:embalagem|caixa|pacote|volume)|^(?:embalagem|caixa|pacote|volume)\b/i;
  const PACKAGING_DETAIL_PATTERN = /^(?:altura|largura|profundidade|comprimento|peso)\s*(?:da|do|de)?\s*(?:embalagem|caixa|pacote|volume)?\s*[:\-]/i;

  const clean = (value = '') => String(value)
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+/g, ' ')
    .replace(/\s*\n\s*/g, '\n')
    .trim();

  const compact = (value = '', limit = 120) => {
    const text = clean(value).replace(/[.;,]+$/, '');
    if (text.length <= limit) return text;
    const slice = text.slice(0, limit + 1);
    const cut = slice.lastIndexOf(' ');
    return `${slice.slice(0, cut > limit * 0.7 ? cut : limit).trim()}…`;
  };

  const prettyMeasure = (value = '') => clean(value)
    .replace(/(\d)\s*(mm|cm|m)\b/gi, '$1 $2')
    .replace(/\s+/g, ' ');

  const inferCodedUnit = (value = '') => {
    const normalized = clean(value);
    if (/(?:mm|cm|m)\b/i.test(normalized)) return prettyMeasure(normalized);

    const number = Number.parseFloat(normalized.replace(',', '.'));
    if (!Number.isFinite(number)) return normalized;
    return `${normalized} ${number <= 4 ? 'm' : 'cm'}`;
  };

  const escapeRegExp = (value = '') => String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const productOnlyText = (text = '') => {
    const lines = clean(text).split('\n').map(clean).filter(Boolean);
    const output = [];
    let packagingRowsRemaining = 0;

    lines.forEach((line) => {
      if (PACKAGING_HEADER_PATTERN.test(line)) {
        packagingRowsRemaining = 6;
        return;
      }

      if (packagingRowsRemaining > 0) {
        const looksLikePackagingValue = PACKAGING_DETAIL_PATTERN.test(line)
          || /^\d{1,4}(?:[.,]\d+)?\s*(?:mm|cm|m|kg)?(?:\s*[xX×]\s*\d{1,4})/i.test(line);

        if (looksLikePackagingValue) {
          packagingRowsRemaining -= 1;
          return;
        }

        packagingRowsRemaining = 0;
      }

      output.push(line);
    });

    return output.join('\n');
  };

  const findByLabel = (text, label) => {
    const excludedSubjects = '(?:assento|encosto|braco|braço|pes|pés|base|colchao|colchão|embalagem|caixa|pacote|volume)';
    const safeLabel = `${label}(?!\\s+(?:do|da|de)\\s+${excludedSubjects})`;
    const after = new RegExp(`\\b${safeLabel}(?:\\s+(?:total|do produto|externa|externo))?\\s*[:\\-]?\\s*(${MEASURE_PATTERN})`, 'i');
    const before = new RegExp(`(${MEASURE_PATTERN})\\s*(?:de\\s+)?${safeLabel}\\b`, 'i');
    const match = text.match(after) || text.match(before);
    return match?.[1] ? prettyMeasure(match[1]) : '';
  };

  const findByCode = (text, code) => {
    const after = new RegExp(`(?:^|[xX×;|,])\\s*\\(?${code}\\)?\\s*[:\\-]?\\s*(\\d{1,4}(?:[.,]\\d{1,2})?\\s*(?:mm|cm|m)?)`, 'im');
    const before = new RegExp(`(\\d{1,4}(?:[.,]\\d{1,2})?\\s*(?:mm|cm|m)?)\\s*\\(?${code}\\)?(?=\\s*(?:x|X|×|;|\\||,|$))`, 'im');
    const match = text.match(after) || text.match(before);
    return match?.[1] ? inferCodedUnit(match[1]) : '';
  };

  const findLabeledText = (text, labels) => {
    for (const label of labels) {
      const safeLabel = escapeRegExp(label).replace(/\s+/g, '\\s+');
      const pattern = new RegExp(`(?:^|\\n|[|;•])\\s*${safeLabel}\\s*[:\\-]\\s*([^\\n|;•]{2,160})`, 'i');
      const match = text.match(pattern);
      if (match?.[1]) return compact(match[1], 135);
    }
    return '';
  };

  const materialRows = (text) => {
    if (!text) return [];

    const definitions = [
      ['Estrutura', ['materiais da estrutura', 'material da estrutura', 'estrutura']],
      ['Revestimento', ['revestimento', 'tecido']],
      ['Espuma', ['densidade da espuma', 'espuma']],
      ['Enchimento', ['enchimento']],
      ['Acabamento', ['acabamento']],
      ['Composição', ['composição', 'composicao', 'materiais', 'material']]
    ];

    return definitions
      .map(([label, aliases]) => ({ label, value: findLabeledText(text, aliases) }))
      .filter(({ value }) => Boolean(value));
  };

  const stateRows = (text) => {
    const rows = [];
    const lines = clean(text).split('\n').map(clean).filter(Boolean);

    for (const line of lines) {
      const match = line.match(/^(fechado|aberto|modo\s+sof[aá]|modo\s+cama)\s*[:\-]\s*(.+)$/i);
      if (!match) continue;

      const measurements = match[2].match(new RegExp(MEASURE_PATTERN, 'gi')) || [];
      const coded = /\d+(?:[.,]\d+)?\s*\(?(?:a|l|p|c)\)?\b/i.test(match[2]);
      const triple = /\d+(?:[.,]\d+)?\s*(?:mm|cm|m)?\s*[xX×]\s*\d+/i.test(match[2]);
      if (measurements.length < 2 && !coded && !triple) continue;

      const label = match[1]
        .replace(/^modo\s+/i, '')
        .replace(/^./, (character) => character.toUpperCase());
      rows.push({ label, value: compact(match[2], 115) });
      if (rows.length === 2) break;
    }

    return rows;
  };

  const dimensionRows = (text) => {
    const definitions = [
      ['Altura', ['altura'], ['A']],
      ['Largura', ['largura'], ['L']],
      ['Profundidade', ['profundidade', 'comprimento'], ['P', 'C']],
      ['Altura do assento', ['altura do assento'], []]
    ];

    return definitions
      .map(([label, words, codes]) => {
        let value = '';

        for (const word of words) {
          value = findByLabel(text, word);
          if (value) break;
        }

        if (!value) {
          for (const code of codes) {
            value = findByCode(text, code);
            if (value) break;
          }
        }

        return { label, value };
      })
      .filter(({ value }) => Boolean(value));
  };

  const extractTriple = (value = '') => {
    const match = clean(value).match(/(\d{1,4}(?:[.,]\d{1,2})?)\s*(mm|cm|m)?\s*[xX×]\s*(\d{1,4}(?:[.,]\d{1,2})?)\s*(mm|cm|m)?\s*[xX×]\s*(\d{1,4}(?:[.,]\d{1,2})?)\s*(mm|cm|m)?/i);
    if (!match) return '';

    const unit = match[2] || match[4] || match[6] || 'cm';
    return `${match[1]} ${match[2] || unit} × ${match[3]} ${match[4] || unit} × ${match[5]} ${match[6] || unit}`;
  };

  const genericDimensionRow = (text) => {
    const lines = clean(text).split('\n').map(clean).filter(Boolean);

    for (let index = 0; index < lines.length; index += 1) {
      const line = lines[index];
      const hasOrderContext = /\b(?:a|altura)\s*[xX×]\s*(?:l|largura)\s*[xX×]\s*(?:c|comprimento)\b/i.test(line)
        || /\(\s*A\s*[xX×]\s*L\s*[xX×]\s*C\s*\)/i.test(line);

      if (!hasOrderContext) continue;

      const candidate = [line, lines[index + 1], lines[index + 2], lines[index + 3]]
        .filter(Boolean)
        .join(' ');
      if (/\b(?:p|profundidade)\b/i.test(candidate)) continue;

      const triple = extractTriple(candidate);
      if (!triple) continue;

      return { label: 'Medidas', value: `${triple} (A × L × C)` };
    }

    return null;
  };

  const mergeUniqueRows = (...groups) => {
    const rows = [];
    const seen = new Set();

    groups.flat().forEach((row) => {
      if (!row?.label || !row?.value) return;
      const key = `${clean(row.label).toLowerCase()}|${clean(row.value).toLowerCase()}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push(row);
    });

    return rows;
  };

  const mergeRowsByLabel = (...groups) => {
    const rows = [];
    const seenLabels = new Set();

    groups.flat().forEach((row) => {
      if (!row?.label || !row?.value) return;
      const labelKey = clean(row.label).toLowerCase();
      if (seenLabels.has(labelKey)) return;
      seenLabels.add(labelKey);
      rows.push(row);
    });

    return rows;
  };

  const rowsFromDimensionsText = (text) => {
    const safeText = productOnlyText(text);
    if (!safeText) return [];

    const states = stateRows(safeText);
    const dimensions = states.length ? [] : dimensionRows(safeText);
    const generic = !states.length && dimensions.length < 2 ? genericDimensionRow(safeText) : null;

    return mergeUniqueRows(
      states,
      dimensions,
      generic ? [generic] : []
    );
  };

  const getDescriptionText = (root) => {
    const mainProduct = root.closest('[id^="MainProduct-"]') || document;
    const description = mainProduct.querySelector('.product__description-section .rte')
      || mainProduct.querySelector('.product__description .rte')
      || document.querySelector('.product__description-section .rte');
    return clean(description?.innerText || description?.textContent || '');
  };

  const renderMaterials = (root) => {
    const target = root.querySelector('[data-mf-materials-target]');
    if (!target) return;

    const source = root.querySelector('[data-mf-source="materials"]');
    const rows = mergeUniqueRows(
      materialRows(clean(source?.textContent || '')),
      materialRows(getDescriptionText(root))
    ).slice(0, 5);

    if (!rows.length) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'mf-quick-specs';

    rows.forEach(({ label, value }) => {
      const paragraph = document.createElement('p');
      const strong = document.createElement('strong');
      strong.textContent = `${label}:`;
      paragraph.append(strong, document.createTextNode(` ${value}`));
      wrapper.appendChild(paragraph);
    });

    target.replaceChildren(wrapper);
  };

  const renderDimensions = (root) => {
    const target = root.querySelector('[data-mf-dimensions-target]');
    if (!target) return;

    const source = root.querySelector('[data-mf-source="dimensions"]');
    const openSource = root.querySelector('[data-mf-source="dimensions-open"]');
    const sourceRows = rowsFromDimensionsText(clean(source?.textContent || ''));
    const descriptionRows = rowsFromDimensionsText(getDescriptionText(root));
    const openRows = rowsFromDimensionsText(clean(openSource?.textContent || ''))
      .filter(({ label }) => /^(?:altura|largura|profundidade|altura do assento)$/i.test(label));

    let rows = mergeRowsByLabel(sourceRows, descriptionRows);
    const hasSpecificDimensions = rows.some(({ label }) => /^(?:altura|largura|profundidade)$/i.test(label));
    if (hasSpecificDimensions) {
      rows = rows.filter(({ label }) => !/^medidas$/i.test(label));
    }
    rows = rows.slice(0, 6);

    target.querySelectorAll('[data-mf-generated-dimension], [data-mf-dimensions-header]').forEach((item) => item.remove());
    target.classList.toggle('mf-quick-specs--dimensions-compare', openRows.length > 0);

    if (openRows.length > 0) {
      target.querySelector('[data-mf-dimensions-placeholder]')?.remove();

      const closedByLabel = new Map(
        rows
          .filter(({ label }) => /^(?:altura|largura|profundidade|altura do assento)$/i.test(label))
          .map(({ label, value }) => [clean(label).toLowerCase(), value])
      );
      const openByLabel = new Map(
        openRows.map(({ label, value }) => [clean(label).toLowerCase(), value])
      );
      const orderedLabels = ['Altura', 'Largura', 'Profundidade', 'Altura do assento']
        .filter((label) => closedByLabel.has(label.toLowerCase()) || openByLabel.has(label.toLowerCase()));

      const fragment = document.createDocumentFragment();
      const header = document.createElement('div');
      header.className = 'mf-dimensions__header';
      header.dataset.mfDimensionsHeader = 'true';

      const measureHeading = document.createElement('span');
      measureHeading.textContent = 'Medida';
      const closedHeading = document.createElement('strong');
      closedHeading.textContent = 'Fechado';
      const openHeading = document.createElement('strong');
      openHeading.textContent = 'Aberto';
      header.append(measureHeading, closedHeading, openHeading);
      fragment.appendChild(header);

      orderedLabels.forEach((label) => {
        const key = label.toLowerCase();
        const paragraph = document.createElement('p');
        paragraph.dataset.mfGeneratedDimension = 'true';
        paragraph.className = 'mf-dimensions__compare-row';

        const strong = document.createElement('strong');
        strong.textContent = `${label}:`;

        const closedValue = document.createElement('span');
        closedValue.className = 'mf-dimensions__value mf-dimensions__value--closed';
        closedValue.textContent = closedByLabel.get(key) || '';

        const openValue = document.createElement('span');
        openValue.className = 'mf-dimensions__value mf-dimensions__value--open';
        openValue.textContent = openByLabel.get(key) || '';

        paragraph.append(strong, closedValue, openValue);
        fragment.appendChild(paragraph);
      });

      const fixedDetailRow = target.querySelector('[data-mf-fixed-detail-row]');
      target.insertBefore(fragment, fixedDetailRow || target.firstChild);
      return;
    }

    if (!rows.length) return;

    target.querySelector('[data-mf-dimensions-placeholder]')?.remove();

    const fragment = document.createDocumentFragment();
    rows.forEach(({ label, value }) => {
      const paragraph = document.createElement('p');
      paragraph.dataset.mfGeneratedDimension = 'true';
      const strong = document.createElement('strong');
      strong.textContent = `${label}:`;
      paragraph.append(strong, document.createTextNode(` ${value}`));
      fragment.appendChild(paragraph);
    });

    const fixedDetailRow = target.querySelector('[data-mf-fixed-detail-row]');
    target.insertBefore(fragment, fixedDetailRow || target.firstChild);
  };

  const init = (root) => {
    if (!root || root.dataset.mfReady === 'true') return;
    root.dataset.mfReady = 'true';

    renderMaterials(root);
    renderDimensions(root);

    root.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        root.querySelectorAll('details[open]').forEach((item) => {
          if (item !== details) item.removeAttribute('open');
        });
      });
    });
  };

  const initAll = (scope = document) => scope.querySelectorAll(ROOT_SELECTOR).forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
